import React, { useCallback, useState } from 'react';
import Chip from '@mui/material/Chip';
import { withTheme } from '@mui/styles';
import {
  COMPOUNDING_ALIAS,
  COMPOUNDING_PERIOD_ALIAS,
  CUMULATIVE_DIVIDENDS_ALIAS,
  DIVIDEND_RATE_ALIAS,
  LIQUIDATION_PREFERENCE_ALIAS,
  LIQUIDATION_PREFERENCE_LEDGER_ALIAS,
  SECURITY_TYPE_ALIAS,
} from 'common/constants/cap-table';
import { PREFERRED_STOCK } from 'common/constants/securityTypes';
import { useFeatureSpreadsheet } from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { useCapTableContext } from 'context/CapTableContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import DisabledVariableChip, { StyledChip } from '../DisabledVariableChip';
import { VariableChipProps } from '../types';
import VariableChipStylesWrapper from '../VariableChipStylesWrapper';

const LiquidationPreferenceVariableChip = ({ theme }: VariableChipProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const { hasVariableLiquidationPreference, setHasVariableLiquidationPreference, hasVariableDividendRate }
    = useCapTableContext();
  const { onCellsChanged, cells: capCells } = useFeatureSpreadsheet();

  /**
   * @description Group cells into securities based on the column letter
   */
  const groupCellsIntoSecurities = useCallback(() => {
    const cellGroupsByLetter = Object.keys(capCells).reduce(
      (acc, cellKey) => {
        const cell = capCells[cellKey];
        const letter = cell.colLegend;
        if (!letter) return acc;

        acc[letter] = acc[letter] || [];
        acc[letter].push(cell);
        return acc;
      },
      {} as Record<string, ICell[]>
    );

    return cellGroupsByLetter;
  }, [capCells]);

  const updateLedgerInfluencedCellsOnVariableDisable = useCallback(() => {
    const ledgerInfluencedCellAliases = [
      LIQUIDATION_PREFERENCE_ALIAS,
      CUMULATIVE_DIVIDENDS_ALIAS,
      DIVIDEND_RATE_ALIAS,
      COMPOUNDING_ALIAS,
      COMPOUNDING_PERIOD_ALIAS,
      LIQUIDATION_PREFERENCE_LEDGER_ALIAS,
    ];

    // capCells are a1: ICell, a2: ICell
    const cellGroupsByLetter = groupCellsIntoSecurities();

    // filter out the securities that are preferred
    const preferredSecurityCellSets = Object.values(cellGroupsByLetter).filter(security => {
      const securityTypeCell = security.find(cell => cell.alias === SECURITY_TYPE_ALIAS);
      return securityTypeCell?.value === PREFERRED_STOCK;
    });

    const changeSet = Object.values(preferredSecurityCellSets).flatMap(cellGroup =>
      // update cell values for ledger influenced cells
      cellGroup
        .filter(cell => ledgerInfluencedCellAliases.includes(cell.alias))
        .map(cell => ({
          cell,
          value: cell.defaultValue,
        }))
    );
    onCellsChanged(changeSet);
  }, [onCellsChanged, groupCellsIntoSecurities]);

  const handleClick = useCallback(() => {
    const newVal = !hasVariableLiquidationPreference;
    if (!newVal) {
      updateLedgerInfluencedCellsOnVariableDisable();
    }
    setHasVariableLiquidationPreference(!hasVariableLiquidationPreference);
  }, [
    hasVariableLiquidationPreference,
    setHasVariableLiquidationPreference,
    updateLedgerInfluencedCellsOnVariableDisable,
  ]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  return (
    <VariableChipStylesWrapper onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {(hasVariableLiquidationPreference || isHovered) && !hasVariableDividendRate && (
        <StyledChip
          style={
            hasVariableLiquidationPreference
              ? { backgroundColor: theme.palette.primary.main, color: theme.palette.white }
              : { backgroundColor: theme.palette.white, color: theme.palette.primary.main }
          }
          label="Variable"
          clickable
          color="primary"
          size="small"
          onClick={handleClick}
          variant="outlined"
        />
      )}
      {hasVariableDividendRate && isHovered && <DisabledVariableChip />}
    </VariableChipStylesWrapper>
  );
};

export default withTheme(LiquidationPreferenceVariableChip);
